import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './button';
import { cn } from '@/lib/utils';

export type EmptyStateProps = {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  /** Primary action · either internal link (`to`) or external (`href`) or onClick. */
  action?:
    | { label: string; to: string; onClick?: never; href?: never }
    | { label: string; onClick: () => void; to?: never; href?: never }
    | { label: string; href: string; to?: never; onClick?: never };
  /** Optional secondary action (rendered as ghost). */
  secondaryAction?: { label: string; to?: string; onClick?: () => void };
  /** Visual treatment of the icon halo. */
  tone?: 'default' | 'error' | 'success' | 'warm';
  /** Padding and max-width preset. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const TONES = {
  default: 'bg-secondary text-primary',
  error: 'bg-rose-100 text-rose-700',
  success: 'bg-emerald-100 text-emerald-700',
  warm: 'bg-amber-100 text-amber-800',
} as const;

const SIZES = {
  sm: { wrapper: 'py-10', icon: 'h-12 w-12', iconSize: 'h-6 w-6' },
  md: { wrapper: 'py-16', icon: 'h-16 w-16', iconSize: 'h-8 w-8' },
  lg: { wrapper: 'py-24 md:py-32', icon: 'h-20 w-20', iconSize: 'h-10 w-10' },
} as const;

/**
 * Visual placeholder for 404s, empty lists, errors, and "nothing here yet"
 * states. One look-and-feel across the whole app.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  tone = 'default',
  size = 'md',
  className,
}: EmptyStateProps) {
  const sz = SIZES[size];
  return (
    <div className={cn('flex flex-col items-center text-center', sz.wrapper, className)}>
      <div
        className={cn(
          'flex items-center justify-center rounded-full',
          sz.icon,
          TONES[tone],
        )}
      >
        <Icon className={sz.iconSize} strokeWidth={2} />
      </div>

      <h2 className="mt-5 text-balance text-xl font-bold tracking-tight md:text-2xl">
        {title}
      </h2>

      {description && (
        <div className="mt-2 max-w-md text-balance text-sm text-muted-foreground md:text-base">
          {description}
        </div>
      )}

      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {action && <PrimaryAction action={action} />}
          {secondaryAction && (
            <Button asChild={!!secondaryAction.to} variant="ghost" onClick={secondaryAction.onClick}>
              {secondaryAction.to ? (
                <Link to={secondaryAction.to}>{secondaryAction.label}</Link>
              ) : (
                <span>{secondaryAction.label}</span>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function PrimaryAction({ action }: { action: NonNullable<EmptyStateProps['action']> }) {
  if (action.to) {
    return (
      <Button asChild>
        <Link to={action.to}>{action.label}</Link>
      </Button>
    );
  }
  if (action.href) {
    return (
      <Button asChild>
        <a href={action.href} target="_blank" rel="noopener noreferrer">
          {action.label}
        </a>
      </Button>
    );
  }
  return <Button onClick={action.onClick}>{action.label}</Button>;
}
